'use client';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { CatalogEventKeys } from '../events';
import { EventBus } from '@/shared/utils';
import { useAuthStore, useAuthenticatedFetch } from '@/auth';
import { createAuthenticatedCatalogServices } from '../services/api-services-authenticated';

export type DocumentType = 'serie' | 'masterclass' | 'learningPath';

interface UseCatalogFavouriteProps {
  eventBus: EventBus<any>;
  documentId: string;
  documentType: DocumentType;
  favouriteId?: string;
  onFavouriteChange?: (courseId: string, favouriteId: string | undefined) => void;
}

// Define response type for the create favorite endpoint
interface FavouriteResponse {
  documentId: string;
  // Add other properties as needed
}

/**
 * Hook for favorites management in the catalog module.
 * Handles creating and deleting favorites for different content types.
 */
export function useCatalogFavourite({
  eventBus,
  documentId,
  documentType,
  favouriteId: initialFavouriteId,
  onFavouriteChange,
}: UseCatalogFavouriteProps) {
  const { user } = useAuthStore();
  const { fetchWithAuth } = useAuthenticatedFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [favouriteId, setFavouriteId] = useState<string | undefined>(initialFavouriteId);

  // Create authenticated services with token refresh
  const authServices = useMemo(
    () => createAuthenticatedCatalogServices(fetchWithAuth, eventBus),
    [fetchWithAuth, eventBus],
  );

  // Sync local favouriteId when prop changes
  useEffect(() => {
    setFavouriteId(initialFavouriteId);
  }, [initialFavouriteId]);

  // Derive isFavourite from favouriteId existence
  const isFavourite = !!favouriteId;

  // Create a new favourite
  const createFavourite = useCallback(async () => {
    // Don't allow favoriting series
    if (documentType === 'serie') {
      console.log('Series cannot be favorited');
      return;
    }

    if (!user?.email) {
      console.error('User not authenticated');
      return;
    }

    setIsLoading(true);

    try {
      // Create the appropriate request body
      const requestBody: Record<string, any> = {
        email: user.email,
      };

      // Add the correct document ID field based on type
      if (documentType === 'masterclass') {
        requestBody.masterclassDocumentId = documentId;
      } else if (documentType === 'learningPath') {
        requestBody.learningPathDocumentId = documentId;
      }

      const result = await authServices.createFavourite(requestBody);

      if (!result.success) {
        throw result.error;
      }

      // Safely extract data with proper type checking
      const responseData = result?.data as any;
      const data = responseData?.data ?? responseData;

      // Set new favorite ID
      const newFavouriteId = data.documentId;
      setFavouriteId(newFavouriteId);

      // Call the callback if provided to update parent components
      if (onFavouriteChange && typeof onFavouriteChange === 'function') {
        onFavouriteChange(documentId, newFavouriteId);
      }

      // Emit success event
      eventBus.emit(CatalogEventKeys.favourite.create[documentType].success, {
        documentId: data.documentId,
      });

      return data;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      eventBus.emit(CatalogEventKeys.favourite.create[documentType].error, errorObj);
      throw errorObj;
    } finally {
      setIsLoading(false);
    }
  }, [documentId, documentType, eventBus, user, authServices, onFavouriteChange]);

  // Delete an existing favourite
  const deleteFavourite = useCallback(async () => {
    if (!favouriteId || !user?.email) {
      console.error('Favourite ID or user not authenticated');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authServices.deleteFavourite(favouriteId, user.email);

      if (!result.success) {
        throw result.error;
      }

      setFavouriteId(undefined);

      // Call the callback if provided to update parent components
      if (onFavouriteChange && typeof onFavouriteChange === 'function') {
        onFavouriteChange(documentId, undefined);
      }

      // Emit success event
      eventBus.emit(CatalogEventKeys.favourite.delete[documentType].success, undefined);

      return true;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      eventBus.emit(CatalogEventKeys.favourite.delete[documentType].error, errorObj);
      throw errorObj;
    } finally {
      setIsLoading(false);
    }
  }, [favouriteId, documentType, eventBus, user, authServices, documentId, onFavouriteChange]);

  // Toggle favourite status
  const toggleFavourite = useCallback(async () => {
    // Don't allow favoriting series
    if (documentType === 'serie') {
      console.log('Series cannot be favorited');
      return;
    }

    if (isFavourite) {
      return deleteFavourite();
    } else {
      return createFavourite();
    }
  }, [isFavourite, deleteFavourite, createFavourite, documentType]);

  return {
    isFavourite,
    isLoading,
    createFavourite,
    deleteFavourite,
    toggleFavourite,
    favouriteId,
  };
}
